/**
 * Admin Dashboard Container
 * Handles navigation between different admin dashboard sections
 * Manages the main dashboard view and individual section views
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import ProfessionalLoader from '../../components/common/ProfessionalLoader';
import Logo from '../../components/common/Logo';

// Import section components
import EnhancedUserManagement from '../../components/users/EnhancedUserManagement';
import MemberList from '../../components/members/MemberList';
import InvoiceManagement from '../../components/invoices/InvoiceManagement';
import IntegrationDashboard from '../../components/integrations/IntegrationDashboard';
import AdminDashboard from './AdminDashboard';

// Import missing components
import ApplicationManagement from '../../components/applications/ApplicationManagement';
import FinancialPeriods from '../../components/financial/FinancialPeriods';
import Distributors from '../../components/distributors/Distributors';

import './AdminDashboardContainer.css';

const AdminDashboardContainer = () => {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }

    if (isAuthenticated && user) {
      const userRole = user.user_role || user.role;
      if (userRole !== 'Superuser' && userRole !== 'Staff') {
        navigate('/user-dashboard');
        return;
      }
    }
  }, [isAuthenticated, isLoading, user, navigate]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    } else {
      setActiveTab('dashboard');
    }
  }, [searchParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'dashboard') {
      navigate('/admin-dashboard');
    } else {
      navigate(`/admin-dashboard?tab=${tab}`);
    }
  };

  const handleBackToDashboard = () => {
    setActiveTab('dashboard');
    navigate('/admin-dashboard');
  };

  const handleLogout = async () => {
    await logout();
  };

  const formatUserEmail = (email) => {
    if (!email) return 'Admin';
    const [username] = email.split('@');
    return username.length > 15 ? `${username.substring(0, 15)}...` : username;
  };

  if (isLoading) {
    return (
      <div className="admin-dashboard-container">
        <div className="dashboard-loading">
          <ProfessionalLoader message="Loading admin dashboard..." size="large" variant="default" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  const userRole = user.user_role || user.role;
  if (userRole !== 'Superuser' && userRole !== 'Staff') {
    return null;
  }

  // Navigation items
  const navigationItems = [
    { id: 'dashboard', label: 'Admin Dashboard', icon: '🏠' },
    { id: 'applications', label: 'Applications', icon: '📝' },
    { id: 'users', label: 'Users', icon: '👥' },
    { id: 'members', label: 'Members', icon: '📋' },
    { id: 'invoices', label: 'Invoices', icon: '💰' },
    { id: 'financial-periods', label: 'Financial Periods', icon: '📅' },
    { id: 'distributors', label: 'Distributors', icon: '🏢' },
    { id: 'integrations', label: 'Integrations', icon: '🔗' },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <AdminDashboard />;
      case 'applications':
        return <ApplicationManagement />;
      case 'users':
        return <EnhancedUserManagement />;
      case 'members':
        return <MemberList userRole="admin" />;
      case 'invoices':
        return <InvoiceManagement userRole="admin" />;
      case 'financial-periods':
        return <FinancialPeriods userRole="admin" />;
      case 'distributors':
        return <Distributors userRole="admin" />;
      case 'integrations':
        return <IntegrationDashboard userRole="admin" />;
      default:
        return <AdminDashboard />;
    }
  };

  return (
    <div className="admin-dashboard-container">
      {/* Navigation Sidebar */}
      <div className="admin-sidebar">
        <div className="sidebar-header">
          <div className="logo">
            <Logo
              className="logo-image"
              showText={true}
              text="AFF Admin"
              textClassName="logo-text"
            />
          </div>
        </div>

        <nav className="sidebar-nav">
          {navigationItems.map((item) => (
            <button
              key={item.id}
              className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
              onClick={() => handleTabChange(item.id)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
        </nav>

        {/* <div className="sidebar-footer">
          <div className="user-section">
            <div className="user-info">
              <div className="user-avatar">
                <span>{user?.email?.charAt(0).toUpperCase() || 'A'}</span>
              </div>
              <div className="user-details">
                <span className="user-name">{formatUserEmail(user?.email)}</span> */}
        {/* <span className="user-role">{userRole}</span> */}
        {/* </div>
            </div>
            <button onClick={handleLogout} className="logout-btn">
              <span className="logout-icon">🚪</span>
              <span className="logout-text">Sign Out</span>
            </button>
          </div>
        </div> */}
      </div>

      {/* Main Content */}
      <div className="admin-main-content">
        {/* Top Bar */}
        {/* <div className="admin-topbar">
          <div className="topbar-left">
            {activeTab !== 'dashboard' && (
              <button
                className="back-to-dashboard-btn"
                onClick={handleBackToDashboard}
              >
                <span className="back-icon">←</span>
                Back to Dashboard
              </button>
            )}
            <h1 className="page-title">
              {navigationItems.find(item => item.id === activeTab)?.label || 'Dashboard'}
            </h1>
          </div>

        </div> */}

        {/* Content Area */}
        <div className="admin-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardContainer;
